/**
 * Particle-flow animation engine — the LIVE-traffic overlay's model.
 *
 * Pure, frame-stepped, and geometry-free: it owns particles traveling 0→1
 * along an edge, spawning at a rate proportional to that edge's live request
 * intensity and despawning at the far end. The *where* (projecting `t` to an
 * (x,y) on the edge's path) is injected by the renderer via a sampler, so this
 * engine stays unit-testable with a fake sampler and carries no DOM/SVG deps.
 *
 * This drives a live-only paint layer (never the scene export) — traffic is a
 * property of the running system, not of the diagram.
 */

/** One dot in flight along an edge. `t` is normalized arc position 0..1. */
export interface FlowParticle {
  readonly edgeId: string;
  /** Normalized position along the edge path, 0 (source) → 1 (target). */
  t: number;
  /** Path length (space units) this particle travels — fixes its `t` speed
   *  so every dot moves at the same on-screen pace regardless of edge length. */
  readonly length: number;
}

/** Live intensity for one edge this frame. `rate` = particles to spawn per
 *  second (the caller normalizes observed requests/sec into this). */
export interface FlowEdgeInput {
  readonly id: string;
  readonly rate: number;
  /** Arc length of the edge path in space units (from the SVG path measure). */
  readonly length: number;
}

export interface FlowState {
  particles: FlowParticle[];
  /** Fractional spawn carry per edge, so sub-1 spawns-per-frame accumulate
   *  instead of rounding to zero (a 3 req/s edge at 60fps spawns correctly). */
  readonly spawnAccum: Map<string, number>;
}

/** Space units per second a dot travels. Tuned so dots read as a steady
 *  stream at typical board zoom without strobing. */
export const FLOW_SPEED = 160;

/** Hard cap on concurrent dots per edge — busy edges saturate visually well
 *  before this; the cap just bounds worst-case DOM/CPU. */
export const MAX_PARTICLES_PER_EDGE = 40;

/** Soft-knee knee point (requests/sec) for {@link flowSpawnRate}. Below it the
 *  dot rate is ~1:1 with real traffic (honest sparseness on quiet systems);
 *  above it the curve is logarithmic so a 10k+ rpm edge stays legible instead of
 *  flooding. Raise it to keep more edges in the near-linear region. */
export const FLOW_RATE_KNEE = 6;

/**
 * Map an edge's ABSOLUTE request rate (req/sec) to a dot spawn rate (dots/sec).
 *
 * A dot is NOT one request — that can't scale to 10k+ rpm. Instead this is a
 * soft-knee compressor: near-linear (dots ≈ requests) at low load, so a quiet
 * edge reads honestly; logarithmic above the knee, so a very busy edge saturates
 * smoothly (and the {@link MAX_PARTICLES_PER_EDGE} cap bounds the rest). Zero in
 * → zero out (no fabricated dots on idle edges).
 *
 *   k=6:  1.6→~1.4   167(10k rpm)→~20   1000(60k rpm)→~31
 */
export function flowSpawnRate(reqPerSec: number, knee: number = FLOW_RATE_KNEE): number {
  if (!(reqPerSec > 0) || knee <= 0) return 0;
  return knee * Math.log1p(reqPerSec / knee);
}

export function createFlowState(): FlowState {
  return { particles: [], spawnAccum: new Map() };
}

/**
 * Advance the simulation by `dtSec`. Spawns new dots per edge ∝ rate, moves
 * every dot forward, and drops any that reached the target. Mutates and
 * returns `state` (one long-lived object per layer, stepped each rAF frame).
 */
export function stepFlow(
  state: FlowState,
  dtSec: number,
  edges: readonly FlowEdgeInput[],
): FlowState {
  if (dtSec <= 0) return state;
  // Clamp dt so a stalled tab (huge dt on resume) doesn't teleport every dot
  // to the end or spawn a burst.
  const dt = Math.min(dtSec, 0.1);

  // Per-edge live count, to enforce the cap on spawn.
  const countByEdge = new Map<string, number>();
  for (const p of state.particles) {
    countByEdge.set(p.edgeId, (countByEdge.get(p.edgeId) ?? 0) + 1);
  }

  const rateById = new Map<string, number>();
  const lengthById = new Map<string, number>();
  for (const e of edges) {
    rateById.set(e.id, e.rate);
    lengthById.set(e.id, e.length);
  }

  // Spawn.
  for (const e of edges) {
    if (e.rate <= 0 || e.length <= 0) continue;
    const accum = (state.spawnAccum.get(e.id) ?? 0) + e.rate * dt;
    let toSpawn = Math.floor(accum);
    state.spawnAccum.set(e.id, accum - toSpawn);
    let live = countByEdge.get(e.id) ?? 0;
    while (toSpawn > 0 && live < MAX_PARTICLES_PER_EDGE) {
      state.particles.push({ edgeId: e.id, t: 0, length: e.length });
      live++;
      toSpawn--;
    }
    countByEdge.set(e.id, live);
  }

  // Advance + despawn (reached target, or the edge no longer exists / went idle
  // and its geometry is gone).
  const next: FlowParticle[] = [];
  for (const p of state.particles) {
    const length = lengthById.get(p.edgeId);
    if (length === undefined) continue; // edge gone from the board
    p.t += (FLOW_SPEED * dt) / p.length;
    if (p.t < 1) next.push(p);
  }
  state.particles = next;

  // Drop spawn carry for edges no longer present, so the map doesn't grow
  // unbounded across model changes.
  for (const id of state.spawnAccum.keys()) {
    if (!rateById.has(id)) state.spawnAccum.delete(id);
  }

  return state;
}
